import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';

import InsertedChip from './components/inserted/';
import ListedChip from './components/listed/';
import ValidatedChip from './components/validated/';
import { Wrapper, RightFade } from './components';
import { modernBrowser } from '../../lib/utils';
import { Show } from '../show';

import chipTypes from '../chips/types';

const DragSelection = styled.div`
    user-select: none;
    opacity: 0.999;
`;

const ActionChip = ({
    badgeIndex,
    disabled,
    id,
    handleClick,
    handleBeginDrag,
    selected,
    type = chipTypes.LISTED,
    validationType,
    value,
    onClose = () => {},
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [overflow, setOverflow] = useState(false);
    const isModernBrowser = modernBrowser();
    let Chip = null;

    const scrolling = (ref) => {
        const offsetWidth = ref.current ? ref.current.offsetWidth : 0;
        const scrollWidth = ref.current ? ref.current.scrollWidth : 0;
        const scrollLeft = ref.current ? ref.current.scrollLeft : 0;
        const scrolledWidth = scrollLeft + offsetWidth;
        setOverflow(scrollWidth > scrolledWidth);
    };

    switch (type) {
        case chipTypes.VALIDATED:
            Chip = ValidatedChip;
            break;
        case chipTypes.INSERTED:
            Chip = InsertedChip;
            break;
        case chipTypes.LISTED:
        default:
            Chip = ListedChip;
    }

    const handleDragStart = useCallback(() => {
        handleBeginDrag();
        setIsDragging(true);
    });

    const handleDragEnd = useCallback(() => {
        handleClick();
        setIsDragging(false);
    });

    return (
        <DragSelection
            draggable={!disabled && [chipTypes.LISTED, chipTypes.INSERTED].includes(type)}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <Wrapper>
                <Chip
                    badgeIndex={badgeIndex}
                    scrolling={scrolling}
                    disabled={disabled}
                    id={id}
                    handleClick={handleClick}
                    value={value}
                    selected={selected}
                    type={validationType}
                    onClose={onClose}
                    isDragging={isDragging}
                    isOver={true}
                />
                <Show when={isModernBrowser}>
                    <RightFade overflowing={overflow} type={type} validationType={validationType} />
                </Show>
            </Wrapper>
        </DragSelection>
    );
};

ActionChip.propTypes = {
    badgeIndex: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    handleClick: PropTypes.func,
    handleBeginDrag: PropTypes.func,
    selected: PropTypes.bool,
    type: PropTypes.oneOf(['listed', 'inserted', 'validated']),
    validationType: PropTypes.oneOf(['error', 'success', 'missing', 'warning']),
    value: PropTypes.string,
    onClose: PropTypes.func,
    isDragging: PropTypes.bool,
};

export default ActionChip;
